import React from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import AdminFormRow from '../components/admin/AdminFormRow';
import AdminMultiValueAutocomplete from '../components/admin/AdminMultiValueAutocomplete';
import AdminSearchField from '../components/admin/AdminSearchField';
import AdminSectionHeading from '../components/admin/AdminSectionHeading';
import AdminStatusPill from '../components/admin/AdminStatusPill';
import AdminSurface from '../components/admin/AdminSurface';
import { adminColors, adminTypography } from '../styles/adminDesignTokens';
import {
  adminAlertSx,
  adminAlertToneSx,
  adminButtonSx,
  adminButtonToneSx,
  adminCardContentSx,
  adminCardHeaderSx,
  adminBottomBannerSx,
  adminCheckboxSx,
  adminCombineSx,
  adminDialogActionsSx,
  adminDialogContentSx,
  adminDialogSx,
  adminDialogTitleSx,
  adminDividerSx,
  adminEmptyStateSx,
  adminFieldLabelSx,
  adminFormErrorTextSx,
  adminFormControlSx,
  adminFormFieldSx,
  adminIconActionButtonSx,
  adminIconTitleSx,
  adminInfoPanelSx,
  adminInlineControlLabelSx,
  adminUploadDropzoneContentSx,
  adminUploadDropzoneSx,
  adminLinkButtonSx,
  adminListViewSx,
  adminLogPanelSx,
  adminPageHeaderSx,
  adminReadOnlyFieldSx,
  adminSidebarNavItemSx,
  adminSidebarSectionLabelSx,
  adminSurfaceSx,
  adminTextLinkSx,
  adminTableSx,
  adminTabsSx,
} from '../styles/adminComponentStyles';

const posTypeOptions = ['Gilbarco Passport', 'Verifone Ruby 2', 'Square for Retail'];
const stateOptions = ['North Carolina', 'South Carolina', 'Georgia', 'Virginia'];
const financialEventOptions = ['Lottery Payout', 'Paid Out', 'Prize Paid'];
const transactionLineOptions = ['Lotto Cash Out', 'Lottery Paid', 'Paid Lottery Winner'];
const lottoMerchandiseOptions = ['Lottery', 'Online Lottery', 'Scratchers'];
const listItems = [
  { id: 'lane-01', title: 'Lane 01', meta: 'Verifone ingest active' },
  { id: 'drawer-close', title: 'Drawer close', meta: 'Last imported 4:02 PM' },
  { id: 'lotto-feed', title: 'Lottery feed', meta: 'Awaiting next store close' },
];
const tableRows = [
  { id: '1001', store: 'Raleigh Market', pos: 'Gilbarco', lotto: true, payout: 'Mapped', status: 'Active' },
  { id: '1002', store: 'Durham Fuel', pos: 'Verifone', lotto: false, payout: 'Needs Review', status: 'Trial' },
  { id: '1003', store: 'Cary Corner', pos: 'Gilbarco', lotto: true, payout: 'Custom', status: 'Pending' },
];

type TableSortKey = 'store' | 'pos' | 'lotto' | 'payout' | 'status';
type TableSortDirection = 'asc' | 'desc';
type TableRowData = typeof tableRows[number];

const tableColumns: Array<{ id: TableSortKey; label: string; align?: 'left' | 'center'; width?: number }> = [
  { id: 'store', label: 'Store' },
  { id: 'pos', label: 'POS' },
  { id: 'lotto', label: 'Lotto', align: 'center', width: 72 },
  { id: 'payout', label: 'Payout Mapping' },
  { id: 'status', label: 'Status' },
];

const getStatusTone = (status: string) => {
  if (status === 'Active') return 'success';
  if (status === 'Trial' || status === 'Pending') return 'warning';
  return 'neutral';
};

const getTableSortValue = (row: TableRowData, key: TableSortKey): string | number => {
  if (key === 'lotto') return row.lotto ? 1 : 0;
  return row[key];
};

const sortTableRows = (
  rows: TableRowData[],
  sortKey: TableSortKey,
  sortDirection: TableSortDirection,
): TableRowData[] => {
  const directionMultiplier = sortDirection === 'asc' ? 1 : -1;

  return [...rows].sort((left, right) => {
    const leftValue = getTableSortValue(left, sortKey);
    const rightValue = getTableSortValue(right, sortKey);

    if (typeof leftValue === 'number' && typeof rightValue === 'number') {
      return (leftValue - rightValue) * directionMultiplier;
    }

    return String(leftValue).localeCompare(String(rightValue)) * directionMultiplier;
  });
};

const AdminStyleGuideExamples: React.FC = () => {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState('overview');
  const [search, setSearch] = React.useState('');
  const [state, setState] = React.useState('North Carolina');
  const [posType, setPosType] = React.useState<string | null>('Gilbarco Passport');
  const [financialEvents, setFinancialEvents] = React.useState(['Lottery Payout', 'Manual Lottery Pay']);
  const [transactionLines, setTransactionLines] = React.useState(['Lotto Cash Out']);
  const [lottoMerchandise, setLottoMerchandise] = React.useState(['Lottery', 'Lottery Online Sale']);
  const [tableSort, setTableSort] = React.useState<{ key: TableSortKey; direction: TableSortDirection }>({
    key: 'store',
    direction: 'asc',
  });

  const sortedTableRows = React.useMemo(
    () => sortTableRows(tableRows, tableSort.key, tableSort.direction),
    [tableSort],
  );

  const handleDialogOpen = React.useCallback(() => setDialogOpen(true), []);
  const handleDialogClose = React.useCallback(() => setDialogOpen(false), []);
  const handleTabChange = React.useCallback((_event: React.SyntheticEvent, nextValue: string) => {
    setActiveTab(nextValue);
  }, []);
  const handleSearchChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  }, []);
  const handleStateChange = React.useCallback((event: SelectChangeEvent) => {
    setState(event.target.value);
  }, []);
  const handlePosTypeChange = React.useCallback((_event: React.SyntheticEvent, nextValue: string | null) => {
    setPosType(nextValue);
  }, []);
  const handleTableSort = React.useCallback((key: TableSortKey) => {
    setTableSort((currentSort) => ({
      key,
      direction: currentSort.key === key && currentSort.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      <Box sx={adminPageHeaderSx}>
        <Box>
          <Typography className="AdminPageHeader-title">Admin Style Guide</Typography>
          <Typography className="AdminPageHeader-meta">Static compact admin examples for shared React/MUI primitives</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<InfoOutlinedIcon />}
            sx={adminCombineSx(adminButtonSx, adminButtonToneSx.secondary)}
            onClick={handleDialogOpen}
          >
            Open Dialog
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddRoundedIcon />}
            sx={adminCombineSx(adminButtonSx, adminButtonToneSx.primary)}
          >
            New Mapping
          </Button>
        </Stack>
      </Box>

      <Stack spacing={2}>
        <AdminSurface sx={adminCardContentSx}>
          <Box sx={adminCardHeaderSx}>
            <Box>
              <Typography className="AdminCardHeader-title">Surface, Header, Divider, Buttons</Typography>
              <Typography className="AdminCardHeader-meta">White surface, compact spacing, no decorative shadow</Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <AdminStatusPill label="Active" tone="success" />
              <AdminStatusPill label="Trial" tone="warning" />
              <AdminStatusPill label="Canceled" tone="danger" />
              <AdminStatusPill label="Info" tone="info" />
              <AdminStatusPill label="Neutral" tone="neutral" />
            </Stack>
          </Box>
          <Divider sx={adminDividerSx} />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
            <Button variant="contained" size="small" sx={adminCombineSx(adminButtonSx, adminButtonToneSx.primary)}>
              Primary
            </Button>
            <Button variant="outlined" size="small" sx={adminCombineSx(adminButtonSx, adminButtonToneSx.secondary)}>
              Secondary
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<DeleteOutlineRoundedIcon />}
              sx={adminCombineSx(adminButtonSx, adminButtonToneSx.danger)}
            >
              Danger
            </Button>
            <Button variant="contained" size="small" disabled sx={adminCombineSx(adminButtonSx, adminButtonToneSx.primary)}>
              Disabled
            </Button>
            <IconButton size="small" sx={adminIconActionButtonSx} aria-label="Edit example">
              <InfoOutlinedIcon fontSize="inherit" />
            </IconButton>
            <Button variant="text" size="small" sx={adminLinkButtonSx}>
              Stores
            </Button>
          </Stack>
        </AdminSurface>

        <AdminSurface sx={{ p: 2 }}>
          <Box sx={adminCardHeaderSx}>
            <Typography className="AdminCardHeader-title">Chrome and Compact Helpers</Typography>
          </Box>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} useFlexGap>
            <Box sx={{ minWidth: 220 }}>
              <Typography sx={adminSidebarSectionLabelSx}>Store</Typography>
              <List dense disablePadding>
                <ListItem disablePadding>
                  <ListItemButton selected sx={adminSidebarNavItemSx}>
                    <ListItemIcon>
                      <CheckCircleRoundedIcon />
                    </ListItemIcon>
                    <ListItemText primary="Stores" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton sx={adminSidebarNavItemSx}>
                    <ListItemIcon>
                      <InfoOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText primary="Audit" />
                  </ListItemButton>
                </ListItem>
              </List>
            </Box>
            <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={adminIconTitleSx}>
                <Box className="AdminIconTitle-icon">
                  <InfoOutlinedIcon fontSize="inherit" />
                </Box>
                <Typography className="AdminIconTitle-title">Icon Title Row</Typography>
              </Box>
              <Typography sx={adminFieldLabelSx}>Field Label</Typography>
              <Link component="button" sx={adminTextLinkSx}>Change Device</Link>
              <FormControlLabel
                control={<Checkbox size="small" checked sx={adminCheckboxSx} />}
                label="Is Enabled"
                sx={adminInlineControlLabelSx}
              />
              <Box sx={adminInfoPanelSx}>
                <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text }}>Info panel</Typography>
                <Typography sx={{ ...adminTypography.body, color: adminColors.mutedText }}>
                  Used for compact setup hints and nested configuration sections.
                </Typography>
              </Box>
              <Box sx={adminBottomBannerSx}>
                <Typography className="AdminBottomBanner-text">Last Seen: 4:02 PM</Typography>
              </Box>
              <Box sx={adminLogPanelSx}>
                <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text }}>Logs updated</Typography>
                <Button variant="outlined" size="small" sx={adminCombineSx(adminButtonSx, adminButtonToneSx.secondary)}>
                  Download
                </Button>
              </Box>
            </Stack>
          </Stack>
        </AdminSurface>

        <AdminSurface sx={{ p: 2 }}>
          <Box sx={adminCardHeaderSx}>
            <Typography className="AdminCardHeader-title">Compact Forms</Typography>
          </Box>
          <Stack spacing={1.25}>
            <AdminSectionHeading>Store Information</AdminSectionHeading>
            <AdminFormRow label="Store Name" required>
              <TextField
                fullWidth
                size="small"
                value="Raleigh Market"
                sx={adminFormFieldSx}
                inputProps={{ 'aria-label': 'Store Name' }}
              />
            </AdminFormRow>
            <AdminFormRow label="Store Key" required>
              <TextField
                error
                fullWidth
                helperText={(
                  <Box component="span" sx={adminFormErrorTextSx}>
                    <WarningAmberRoundedIcon fontSize="inherit" />
                    Store key is required before activation
                  </Box>
                )}
                size="small"
                value=""
                placeholder="store_key"
                sx={adminFormFieldSx}
                inputProps={{ 'aria-label': 'Store Key' }}
              />
            </AdminFormRow>
            <AdminFormRow label="State" required>
              <FormControl fullWidth size="small" sx={adminFormControlSx}>
                <InputLabel id="admin-style-state-label">State</InputLabel>
                <Select
                  labelId="admin-style-state-label"
                  label="State"
                  value={state}
                  onChange={handleStateChange}
                  inputProps={{ 'aria-label': 'State' }}
                >
                  {stateOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Compact select helper text</FormHelperText>
              </FormControl>
            </AdminFormRow>
            <AdminFormRow label="Country">
              <Typography sx={adminReadOnlyFieldSx}>United States</Typography>
            </AdminFormRow>
            <AdminFormRow label="Search">
              <AdminSearchField
                value={search}
                onChange={handleSearchChange}
                placeholder="Search stores, POS systems, or mappings"
                inputProps={{ 'aria-label': 'Search' }}
              />
            </AdminFormRow>
            <AdminFormRow label="Upload">
              <Box sx={adminUploadDropzoneSx}>
                <Box sx={adminUploadDropzoneContentSx}>
                  <UploadFileRoundedIcon fontSize="small" />
                  <Typography className="AdminUploadDropzone-label">Upload CSV or image</Typography>
                  <Typography className="AdminUploadDropzone-helper">Compact dashed upload surface</Typography>
                </Box>
              </Box>
            </AdminFormRow>
            <AdminFormRow label="POS Type">
              <Autocomplete
                size="small"
                options={posTypeOptions}
                value={posType}
                onChange={handlePosTypeChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="POS Type"
                    sx={adminFormFieldSx}
                    inputProps={{
                      ...params.inputProps,
                      'aria-label': 'POS Type',
                    }}
                  />
                )}
              />
            </AdminFormRow>

            <Divider sx={adminCombineSx(adminDividerSx, { my: 0.5 })} />
            <AdminSectionHeading>POS Payout & Lotto Mapping</AdminSectionHeading>
            <AdminFormRow label="Payout Financial Event">
              <AdminMultiValueAutocomplete
                ariaLabel="Payout Financial Event"
                dataOptions={financialEventOptions}
                value={financialEvents}
                onChange={setFinancialEvents}
                helperText={(
                  <Box component="span" sx={{ alignItems: 'center', color: adminColors.warning, display: 'inline-flex', gap: 0.5 }}>
                    <WarningAmberRoundedIcon fontSize="inherit" />
                    Custom values are marked for review
                  </Box>
                )}
              />
            </AdminFormRow>
            <AdminFormRow label="Payout Transaction Line">
              <AdminMultiValueAutocomplete
                ariaLabel="Payout Transaction Line"
                dataOptions={transactionLineOptions}
                value={transactionLines}
                onChange={setTransactionLines}
              />
            </AdminFormRow>
            <AdminFormRow label="Lotto Sales Merchandise">
              <AdminMultiValueAutocomplete
                ariaLabel="Lotto Sales Merchandise"
                dataOptions={lottoMerchandiseOptions}
                value={lottoMerchandise}
                onChange={setLottoMerchandise}
              />
            </AdminFormRow>
          </Stack>
        </AdminSurface>

        <AdminSurface sx={{ p: 0, overflow: 'hidden' }}>
          <Box sx={{ px: 2, pt: 1.5 }}>
            <Box sx={adminCardHeaderSx}>
              <Typography className="AdminCardHeader-title">Compact Sortable Table</Typography>
            </Box>
          </Box>
          <TableContainer>
            <Table size="small" sx={adminTableSx}>
              <TableHead>
                <TableRow>
                  {tableColumns.map((column) => {
                    const isActive = tableSort.key === column.id;
                    return (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        sortDirection={isActive ? tableSort.direction : false}
                        sx={column.width ? { maxWidth: column.width, minWidth: column.width, width: column.width } : undefined}
                      >
                        <TableSortLabel
                          active={isActive}
                          direction={isActive ? tableSort.direction : 'asc'}
                          hideSortIcon={false}
                          onClick={() => handleTableSort(column.id)}
                          sx={{
                            justifyContent: column.align === 'center' ? 'center' : 'flex-start',
                            width: column.align === 'center' ? 'fit-content' : '100%',
                          }}
                        >
                          {column.label}
                        </TableSortLabel>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedTableRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.store}</TableCell>
                    <TableCell>{row.pos}</TableCell>
                    <TableCell align="center" sx={{ maxWidth: 72, minWidth: 72, width: 72 }}>
                      <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', lineHeight: 0, width: '100%' }}>
                        {row.lotto ? (
                          <CheckCircleRoundedIcon sx={{ color: adminColors.success, fontSize: 18 }} />
                        ) : (
                          <CloseRoundedIcon sx={{ color: adminColors.danger, fontSize: 18 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <AdminStatusPill
                        label={row.payout}
                        tone={row.payout === 'Mapped' ? 'success' : row.payout === 'Needs Review' ? 'warning' : 'info'}
                      />
                    </TableCell>
                    <TableCell>
                      <AdminStatusPill label={row.status} tone={getStatusTone(row.status)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AdminSurface>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={adminListViewSx}>
              <List dense disablePadding>
                {listItems.map((item) => (
                  <ListItem key={item.id}>
                    <ListItemText primary={item.title} secondary={item.meta} />
                    <AdminStatusPill label="Ready" tone="success" />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>

          <AdminSurface sx={{ flex: 1, minWidth: 0, p: 2 }}>
            <Box sx={adminCardHeaderSx}>
              <Typography className="AdminCardHeader-title">Tabs, Alerts, Loading, Empty</Typography>
            </Box>
            <Tabs value={activeTab} onChange={handleTabChange} sx={adminTabsSx}>
              <Tab label="Overview" value="overview" />
              <Tab label="Users" value="users" />
              <Tab label="Audits" value="audits" />
            </Tabs>
            <Stack spacing={1.25} sx={{ mt: 1.5 }}>
              <Alert severity="info" icon={<InfoOutlinedIcon />} sx={adminCombineSx(adminAlertSx, adminAlertToneSx.info)}>
                Store close reports are queued for import.
              </Alert>
              <Alert severity="warning" icon={<WarningAmberRoundedIcon />} sx={adminCombineSx(adminAlertSx, adminAlertToneSx.warning)}>
                One custom payout mapping is not present in POS data.
              </Alert>
              <Alert severity="error" icon={<WarningAmberRoundedIcon />} sx={adminCombineSx(adminAlertSx, adminAlertToneSx.danger)}>
                Store mapping could not be saved. Review highlighted fields and try again.
              </Alert>
              <Box sx={adminCombineSx(adminSurfaceSx, { alignItems: 'center', display: 'flex', gap: 1, p: 1.5 })}>
                <CircularProgress size={20} />
                <Typography sx={{ ...adminTypography.body, color: adminColors.mutedText }}>
                  Loading store configuration
                </Typography>
              </Box>
              <Box sx={adminEmptyStateSx}>
                <Typography className="AdminEmptyState-title">No manual entries</Typography>
                <Typography className="AdminEmptyState-copy">Entries created for this store will appear here.</Typography>
              </Box>
            </Stack>
          </AdminSurface>
        </Stack>
      </Stack>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: adminDialogSx }}
      >
        <DialogTitle sx={adminDialogTitleSx}>Compact Admin Dialog</DialogTitle>
        <DialogContent sx={adminDialogContentSx}>
          <Stack spacing={1.25}>
            <AdminSectionHeading>Dialog Fields</AdminSectionHeading>
            <AdminFormRow label="Store Key">
              <TextField
                fullWidth
                size="small"
                value="store_raleigh_1001"
                sx={adminFormFieldSx}
                inputProps={{ 'aria-label': 'Store Key' }}
              />
            </AdminFormRow>
            <AdminFormRow label="Status">
              <AdminStatusPill label="Pending Activation" tone="warning" />
            </AdminFormRow>
          </Stack>
        </DialogContent>
        <DialogActions sx={adminDialogActionsSx}>
          <Button
            variant="outlined"
            size="small"
            sx={adminCombineSx(adminButtonSx, adminButtonToneSx.secondary)}
            onClick={handleDialogClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            sx={adminCombineSx(adminButtonSx, adminButtonToneSx.primary)}
            onClick={handleDialogClose}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminStyleGuideExamples;
